import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ProductCard from '@/components/ProductCard';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Download, ChevronDown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DownloadsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [downloads, setDownloads] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [downloadsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/downloads`),
        axios.get(`${API}/categories`)
      ]);
      setDownloads(downloadsRes.data || []);
      setCategories(categoriesRes.data.downloads || []);
    } catch (err) {
      console.error('Failed to fetch downloads:', err);
      setError('Unable to load downloads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredDownloads = downloads.filter(download => {
    const matchesSearch = download.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          download.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || download.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    if (value === 'all') {
      searchParams.delete('category');
    } else {
      searchParams.set('category', value);
    }
    setSearchParams(searchParams);
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Header />
      
      {/* Hero */}
      <section className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[#1B93A4] flex items-center justify-center">
              <Download className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-[#1A202C]">Digital Downloads</h1>
          </div>
          <p className="text-[#64748B] max-w-2xl">
            Practical tools for immediate impact. Templates, workbooks, and resources to help you take control of your finances.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 lg:top-20 z-40 bg-white/80 backdrop-blur-md border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B]" />
              <Input
                type="text"
                placeholder="Search downloads..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-white border-[#E2E8F0] focus:border-[#2D4A5E] focus:ring-[#2D4A5E]/20 rounded-lg h-12"
                data-testid="download-search-input"
              />
            </div>
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full sm:w-48 h-12 appearance-none bg-white border border-[#E2E8F0] rounded-lg px-4 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4A5E]/20 focus:border-[#2D4A5E]"
                data-testid="category-filter"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-8 lg:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-6">
            <p className="text-[#64748B]">
              Showing <span className="font-medium text-[#1A202C]">{filteredDownloads.length}</span> resources
            </p>
            {selectedCategory !== 'all' && (
              <Badge variant="secondary" className="gap-2">
                {selectedCategory}
                <button onClick={() => handleCategoryChange('all')} className="hover:text-red-500">×</button>
              </Badge>
            )}
          </div>

          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="bg-white animate-pulse rounded-xl h-[350px]"></div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <Download className="w-16 h-16 mx-auto text-red-300 mb-4" />
              <h3 className="text-xl font-semibold text-[#1A202C] mb-2">Error Loading Downloads</h3>
              <p className="text-[#64748B] mb-4">{error}</p>
              <Button 
                onClick={fetchData}
                className="bg-[#1B93A4] hover:bg-[#1A202C]"
              >
                Try Again
              </Button>
            </div>
          ) : filteredDownloads.length === 0 ? (
            <div className="text-center py-16">
              <Download className="w-16 h-16 mx-auto text-[#CBD5E1] mb-4" />
              <h3 className="text-xl font-semibold text-[#1A202C] mb-2">No downloads found</h3>
              <p className="text-[#64748B]">Try adjusting your search or filter criteria</p>
              <Button 
                onClick={() => { setSearchQuery(''); handleCategoryChange('all'); }}
                variant="outline" 
                className="mt-4"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredDownloads.map(download => (
                <ProductCard key={download.product_id} product={download} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DownloadsPage;
